import { Suspense, useCallback, useMemo } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { Stepper, SectionSkeleton } from "@stacklet/ui";
import { ErrorBoundary } from "react-error-boundary";
import { FormProvider, useForm } from "react-hook-form";
import { graphql, useFragment } from "react-relay";
import { useLocation, useNavigate } from "react-router";

import { paths } from "app/constants/paths";
import { useStepNavigation } from "app/hooks/grid/useStepNavigation";

import { NOTIFICATION_RULE_STEP_IDS, NOTIFICATION_RULE_STEPS } from "../consts";
import { useNotificationRuleFormContext } from "../contexts/NotificationRuleFormContext";
import { mapNotificationRuleToFormValues } from "../utils/mapNotificationRuleToFormValues";
import { RulesDeliveryMethod } from "./RulesDeliveryMethod";
import { RulesFilters } from "./RulesFilters";
import { notificationRuleFormSchema, type RulesFilterFormData } from "./schema";

import type { UpsertNotificationRuleStepsContainer_notificationRule$key } from "./__generated__/UpsertNotificationRuleStepsContainer_notificationRule.graphql";

interface Props {
  notificationRule?: UpsertNotificationRuleStepsContainer_notificationRule$key;
}

export function UpsertNotificationRuleStepsContainer({
  notificationRule,
}: Props) {
  const navigate = useNavigate();
  const { state: locationState } = useLocation();
  const { currentStep, navigateToStep } = useStepNavigation({
    stepIds: NOTIFICATION_RULE_STEP_IDS,
  });
  const { isEditMode, ruleUUID } = useNotificationRuleFormContext();
  const data = useFragment(
    graphql`
      fragment UpsertNotificationRuleStepsContainer_notificationRule on NotificationRule {
        uuid
        name
        description
        filterJSON
        groupBy
        includeCostData
        updateSchedule
        target {
          ... on NotificationRuleJiraTarget {
            instance {
              uuid
            }
            space {
              type
              value
            }
            workType
            assignee {
              type
              value
            }
            description
          }
        }
      }
    `,
    notificationRule,
  );
  const mappedRule = useMemo(() => {
    return data
      ? {
          uuid: data.uuid,
          name: data.name,
          description: data.description,
          filterJSON: data.filterJSON,
          groupBy: [...data.groupBy],
          includeCostData: data.includeCostData,
          updateSchedule: data.updateSchedule,
          target: data.target
            ? {
                ...(data.target.assignee
                  ? {
                      assignee: {
                        type: data.target.assignee.type,
                        value: data.target.assignee.value,
                      },
                    }
                  : {}),
                ...(data.target.space
                  ? {
                      space: {
                        type: data.target.space.type,
                        value: data.target.space.value,
                      },
                    }
                  : {}),
                ...(data.target.instance !== undefined
                  ? { instance: data.target.instance }
                  : {}),
                ...(data.target.description !== undefined
                  ? { description: data.target.description }
                  : {}),
                ...(data.target.workType !== undefined
                  ? { workType: data.target.workType }
                  : {}),
              }
            : null,
        }
      : null;
  }, [data]);

  const initialValues = useMemo(() => {
    // Edit mode: map API rule shape -> form shape (dropdown-friendly).
    // Create mode: fall back to empty defaults.
    if (isEditMode && ruleUUID) {
      return mapNotificationRuleToFormValues(mappedRule);
    }

    return mapNotificationRuleToFormValues(null);
  }, [mappedRule, isEditMode, ruleUUID]);

  const methods = useForm<RulesFilterFormData>({
    resolver: zodResolver(notificationRuleFormSchema),
    defaultValues: initialValues,
  });

  const navigateToRules = useCallback(() => {
    const from =
      typeof locationState?.from === "string" ? locationState.from : undefined;
    if (from?.startsWith(`${paths.notifications}/`)) {
      navigate(-1);
    } else {
      navigate(paths.notificationRules);
    }
  }, [locationState, navigate]);

  const stepContent =
    currentStep === 0 ? (
      <Suspense fallback={<SectionSkeleton label="Loading filters…" />}>
        <RulesFilters
          onCancel={navigateToRules}
          onNextStep={() => navigateToStep(1)}
        />
      </Suspense>
    ) : (
      <Suspense fallback={<SectionSkeleton label="Loading delivery method…" />}>
        <RulesDeliveryMethod onCancel={navigateToRules} />
      </Suspense>
    );

  return (
    <FormProvider {...methods}>
      <Stepper
        className="mb-xl"
        currentStep={currentStep}
        onStepClick={navigateToStep}
        steps={NOTIFICATION_RULE_STEPS}
        allowSkipping
      />
      <ErrorBoundary fallback={<div>Error loading step</div>}>
        {stepContent}
      </ErrorBoundary>
    </FormProvider>
  );
}
